import { nullToUndefined } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { type ComponentProps, useState } from 'react';
import type * as Yup from 'yup';

import { BenefitCheckDialog } from 'components/intakeForm/dialogs/BenefitCheckDialog';
import { useGetConfiguredSegmentIdsQuery } from 'store/api/intakeCaseApi';
import { useGetSegmentsQuery } from 'store/api/referenceApi';
import { useLazyGetBenefitCheckQuery } from 'store/api/scopeAssessorApi';
import {
    type FormSections,
    type IntakeFormSectionSchemas,
} from 'store/intakeForm/intakeFormSchema';

type Section = typeof FormSections.HEALTH_PLAN_AND_PATIENT;

interface HealthPlanAndPatientInputsProps {
    onConfirmPatient?: () => void;
    disabled?: boolean;
    healthPlanRef?: ComponentProps<typeof SuggestSingleSelectFormik>['inputRef'];
}

export const HealthPlanAndPatientInputs = ({
    onConfirmPatient = undefined,
    disabled = false,
    healthPlanRef = undefined,
}: HealthPlanAndPatientInputsProps) => {
    const formik = useFormikContext<Yup.InferType<(typeof IntakeFormSectionSchemas)[Section]>>();

    const [showBenefitCheckDialog, setShowBenefitCheckDialog] = useState(false);
    const [isCheckingBenefits, setIsCheckingBenefits] = useState(false);
    const [getBenefitCheck, { data: benefitCheckResponse }] = useLazyGetBenefitCheckQuery();

    const { data: configuredSegmentIds, isFetching: configuredSegmentIdsFetching } =
        useGetConfiguredSegmentIdsQuery();
    const { data: segments, isFetching: segmentsFetching } = useGetSegmentsQuery();

    return (
        <>
            <FullscreenLoading
                isShowing={isCheckingBenefits}
                loadingText="Checking patient benefits and eligibility..."
            />
            <BenefitCheckDialog
                isOpen={showBenefitCheckDialog}
                benefitCheckResponse={benefitCheckResponse}
                onSecondaryButtonClick={() => setShowBenefitCheckDialog(false)}
                onPrimaryButtonClick={() => {
                    void (async () => {
                        setShowBenefitCheckDialog(false);
                        await formik.setValues({
                            ...formik.values,
                            // Use values from benefit check
                            memberFirstName:
                                benefitCheckResponse?.patient_first_name ??
                                formik.values.memberFirstName,
                            memberLastName:
                                benefitCheckResponse?.patient_last_name ??
                                formik.values.memberLastName,
                            memberPhoneNumber: formik.values.benefitCheckPhone,
                            memberCity: benefitCheckResponse?.member_city,
                            memberState: benefitCheckResponse?.member_state,
                            memberZip: benefitCheckResponse?.member_zip,
                            memberVerified: !!benefitCheckResponse,
                            memberEligible: benefitCheckResponse?.is_eligible ?? false,
                        });
                        onConfirmPatient?.();
                    })();
                }}
            />
            <SuggestSingleSelectFormik
                id="segmentId"
                label="Health Plan"
                required
                disabled={disabled}
                inputRef={healthPlanRef}
                isLoading={configuredSegmentIdsFetching || segmentsFetching}
                values={segments?.filter(({ segmentId }) =>
                    configuredSegmentIds?.includes(segmentId),
                )}
                onChange={(newValue) => {
                    void Promise.all([
                        formik.setFieldValue('segmentId', newValue?.segmentId),
                        formik.setFieldValue('payerId', newValue?.payerId),
                    ]);
                }}
                selectedItem={segments?.find(
                    (segment) => segment.segmentId === formik.values.segmentId,
                )}
            />
            <div className="sds_intake-form-section__row">
                <TextInputFormik
                    id="memberFirstName"
                    label="Patient First Name"
                    required
                    disabled={disabled}
                />
                <TextInputFormik
                    id="memberLastName"
                    label="Patient Last Name"
                    required
                    disabled={disabled}
                />
            </div>
            <DateInputFormik
                id="memberDateOfBirth"
                label="Patient Date Of Birth"
                required
                disabled={disabled}
                maxDate={dayjs().endOf('day').toDate()}
            />
            <TextInputFormik id="memberId" label="Member ID" required disabled={disabled} />
            {!disabled && (
                <Button
                    intent={Button.Intent.PRIMARY}
                    id="benefit-check-button"
                    label="Benefit Check"
                    disabled={
                        !!formik.errors.segmentId ||
                        !!formik.errors.memberFirstName ||
                        !!formik.errors.memberLastName ||
                        !!formik.errors.memberDateOfBirth ||
                        !!formik.errors.memberId ||
                        !formik.dirty
                    }
                    onClick={() => {
                        void (async () => {
                            try {
                                setIsCheckingBenefits(true);
                                const result = await getBenefitCheck(formik.values).unwrap();
                                await formik.setFieldValue(
                                    'benefitCheckPhone',
                                    nullToUndefined(result?.member_phone),
                                );
                                setShowBenefitCheckDialog(true);
                            } catch {
                                setShowBenefitCheckDialog(false);
                            } finally {
                                setIsCheckingBenefits(false);
                            }
                        })();
                    }}
                />
            )}
        </>
    );
};
